import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';

/**
 * <nfc-app-shell> — the app frame: one scrolling page and nothing else.
 *
 * The top bar is GONE (operator decision, 2026-08-23): navigation, language
 * and every tool live behind the app's single FAB, so the frame's whole job
 * is a full-bleed scroller that starts at the device's safe area — the shape
 * of a native app, which is what an installed PWA is supposed to feel like.
 * The app's name appears nowhere persistent, exactly like a native app: the
 * home-screen icon is the title.
 *
 * Slots: `nav` (a bar below the page, unused today) and the default slot for
 * the page itself.
 */
@customElement('nfc-app-shell')
export class NfcAppShell extends LitElement {
  static styles = css`
    :host{ display:block; height:100%; }
    .screen{ height:100%; display:flex; flex-direction:column; background:var(--app-bg,#f4f6f9); color:var(--app-text,#14161b);
      font-family:var(--font-plain,system-ui,sans-serif); overflow:hidden; }
    /* The install banner is fixed over the bottom of this scroller, so the
       page has to end above it or the last section is unreachable. The banner
       measures itself into --install-bar-h; the extra gutter also gives the
       final section somewhere to scroll to, so jumping to it actually moves. */
    .content{ flex:1; overflow-y:auto; overflow-x:hidden; -webkit-overflow-scrolling:touch;
      padding-top:calc(var(--safe-top,0px) + 4px);
      padding-bottom:calc(var(--safe-bottom,0px) + var(--install-bar-h,0px) + 24px); }
  `;

  /**
   * Scroll the page back to the top.
   *
   * The scrolling element is `.content` inside this shadow root, not the
   * document, so neither `window.scrollTo` nor the browser's own history scroll
   * restoration can reach it. Callers that swap what the page shows have to ask.
   */
  scrollToTop() {
    (this.renderRoot.querySelector('.content') as HTMLElement | null)?.scrollTo({ top: 0 });
  }

  render() {
    return html`
    <div class="screen">
      <main class="content"><slot></slot></main>
      <slot name="nav"></slot>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'nfc-app-shell': NfcAppShell; }
}
